import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { initMercadoPago, CardPayment } from "@mercadopago/sdk-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Layout } from "../../components/Layout";
import { Button } from "../../components/ui/button";
import { useLang } from "../../contexts/LangContext";
import { useAuth } from "../../contexts/AuthContext";
import { billingService } from "../../services/billingService";
import { formatMoney } from "../../lib/api";
import { LumfiPayLockup } from "../../components/LumfiLogo";
import {
  ArrowLeft, ShieldCheck, Package2, Hourglass, Infinity as InfinityIcon,
  Sparkles, CheckCircle2, Clock, XCircle,
} from "lucide-react";

// Initialize the MP SDK once at module load. The public key comes from the
// frontend env (set during deploy + stored in /app/frontend/.env). If it's
// absent the SDK simply won't mount — we surface a friendly error below.
let _mpInitialized = false;
function ensureMpInit() {
  if (_mpInitialized) return true;
  const pk = process.env.REACT_APP_MP_PUBLIC_KEY;
  if (!pk) return false;
  initMercadoPago(pk, { locale: "es-AR" });
  _mpInitialized = true;
  return true;
}

export default function PackCheckout() {
  const { packId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lang } = useLang();
  const isEs = lang !== "en";
  const isPt = lang === "pt";
  const [result, setResult] = useState(null); // {status, mp_order_id, ...}
  const [sdkReady] = useState(() => ensureMpInit());

  const { data, isLoading, error } = useQuery({
    queryKey: ["billing-pack", packId],
    queryFn: () => billingService.getPack(packId),
    enabled: !!packId,
    retry: false,
  });
  const pack = data?.pack;

  const buy = useMutation({
    mutationFn: (formData) => billingService.buyPackOrder({
      pack_id: packId,
      token: formData.token,
      payment_method_id: formData.payment_method_id,
      installments: formData.installments || 1,
      identification: formData.payer?.identification || null,
      payer_email: formData.payer?.email,
    }),
    onSuccess: (r) => {
      setResult(r);
      if (r.status === "approved") {
        toast.success(isEs ? "¡Pago aprobado! Tu pack está listo." : "Payment approved! Your pack is ready.");
      } else {
        toast.info(isEs ? "Pago en proceso. Te avisamos cuando MP confirme." : "Payment processing. We'll notify you when MP confirms.");
      }
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail || "";
      toast.error(detail.startsWith("mp_orders_error") ? (isEs ? `Error de pago: ${detail.slice(17)}` : `Payment error: ${detail.slice(17)}`) : (isEs ? "El pago no se pudo completar. Reintenta con otra tarjeta." : "Payment couldn't be completed. Try another card."));
    },
  });

  useEffect(() => { if (!user) navigate("/login"); }, [user, navigate]);

  const T = {
    title: isPt ? "Finalizar compra" : isEs ? "Finalizar compra" : "Complete your purchase",
    summary: isPt ? "Resumo" : isEs ? "Resumen" : "Summary",
    units: isEs ? "Unidades" : "Units",
    validity: isEs ? "Validez" : "Validity",
    unlimited: isEs ? "Ilimitado" : "Unlimited",
    days: isEs ? "días" : "days",
    secure: isEs
      ? "Tarjeta procesada de forma certificada · 256-bit SSL"
      : "Card processed via certified gateway · 256-bit SSL",
    back: isEs ? "Volver a planes" : "Back to plans",
    noSdk: isEs ? "El módulo de pagos no está configurado. Contacta soporte." : "Payment module not configured. Contact support.",
    notFound: isEs ? "Pack no encontrado o no disponible en tu país." : "Pack not found or unavailable in your country.",
    seeMyPlan: isEs ? "Ver mi plan" : "View my plan",
    backToDash: isEs ? "Ir al dashboard" : "Back to dashboard",
  };

  if (isLoading) return <Layout><div className="text-center text-slate-400 py-20">…</div></Layout>;
  if (error || !pack) {
    return (
      <Layout>
        <div className="max-w-md mx-auto text-center py-16 fade-up" data-testid="pack-checkout-not-found">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mb-5">
            <XCircle className="h-7 w-7" />
          </div>
          <h1 className="font-serif-display text-3xl font-medium">{T.notFound}</h1>
          <Link to="/pricing" className="inline-block mt-6">
            <Button variant="outline" className="gap-2 rounded-full">
              <ArrowLeft className="h-4 w-4" /> {T.back}
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  // Render confirmation state after a successful submit.
  if (result) return <CheckoutResult result={result} pack={pack} T={T} isEs={isEs} navigate={navigate} />;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto fade-up" data-testid="pack-checkout-page">
        <Link to="/pricing" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6">
          <ArrowLeft className="h-4 w-4" /> {T.back}
        </Link>
        <div className="grid lg:grid-cols-5 gap-7">
          {/* Order summary (2/5) */}
          <div className="lg:col-span-2">
            <div className="rounded-3xl border border-cream-300 bg-white p-7 sticky top-6">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700">{T.summary}</div>
              <h1 className="font-serif-display text-3xl font-medium tracking-tight text-slate-900 mt-3 leading-tight">
                {pack.name}
              </h1>
              {pack.description_note && (
                <p className="text-sm text-slate-600 mt-2 leading-relaxed italic">{pack.description_note}</p>
              )}
              <div className="mt-6 grid grid-cols-2 gap-3">
                <SummaryStat icon={Package2} label={T.units} value={pack.units > 0 ? pack.units : <InfinityIcon className="h-4 w-4 inline" />} />
                <SummaryStat icon={Hourglass} label={T.validity} value={pack.valid_for_days > 0 ? `${pack.valid_for_days} ${T.days}` : T.unlimited} />
              </div>
              {pack.units > 0 && (
                <div
                  className="mt-4 px-3 py-3 rounded-2xl bg-slate-50 border border-slate-100"
                  data-testid="pack-checkout-credit-math"
                >
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1.5">
                    {isEs ? "Con tus unidades puedes" : "With your units you can"}
                  </div>
                  <div className="flex items-center justify-around gap-2 text-center">
                    <div className="flex flex-col items-center gap-0.5">
                      <span className="font-mono-num text-lg font-semibold text-slate-900">
                        ~{Math.floor(pack.units / 1.5)}
                      </span>
                      <span className="text-[10px] text-slate-500">
                        📄 {isEs ? "recibos" : "receipts"}
                      </span>
                    </div>
                    <span className="text-slate-300 text-xs">+</span>
                    <div className="flex flex-col items-center gap-0.5">
                      <span className="font-mono-num text-lg font-semibold text-slate-900">
                        ~{Math.floor(pack.units / 2)}
                      </span>
                      <span className="text-[10px] text-slate-500">
                        💬 {isEs ? "chats" : "chats"}
                      </span>
                    </div>
                  </div>
                  <p className="text-[9px] text-slate-400 mt-2 text-center italic">
                    {isEs ? "Estimaciones · uso real puede variar" : "Estimates · real use may vary"}
                  </p>
                </div>
              )}
              <div className="mt-7 pt-6 border-t border-cream-300">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Total</div>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="font-mono-num text-4xl font-medium text-slate-900">
                    {formatMoney(pack.price_amount, pack.currency)}
                  </span>
                </div>
                <p className="text-[10px] text-emerald-700 font-medium mt-1.5">
                  ✓ {isEs ? "Pago único · no se renueva" : "One-time payment · no renewal"}
                </p>
              </div>
              <p className="text-[11px] text-slate-400 mt-5 flex items-start gap-1.5 leading-snug" data-testid="pack-checkout-secure-line">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
                {T.secure}
              </p>
              <div className="mt-3 pt-3 border-t border-cream-300/60" data-testid="pack-checkout-lumfi-pay">
                <LumfiPayLockup variant="light" height={26} className="opacity-90" />
              </div>
            </div>
          </div>

          {/* Bricks card payment (3/5) */}
          <div className="lg:col-span-3">
            <div className="rounded-3xl border border-cream-300 bg-white p-6 lg:p-8 min-h-[460px]" data-testid="pack-checkout-brick-container">
              {!sdkReady ? (
                <div className="text-center py-12 text-rose-600" data-testid="pack-checkout-no-sdk">{T.noSdk}</div>
              ) : (
                <CardPayment
                  initialization={{
                    amount: Number(pack.price_amount),
                    payer: { email: user?.email },
                  }}
                  customization={{
                    paymentMethods: { maxInstallments: 12 },
                    visual: {
                      style: {
                        theme: "default",
                        customVariables: {
                          baseColor: "#16A34A",
                          buttonTextColor: "#FFFFFF",
                          formBackgroundColor: "#FFFFFF",
                          borderRadiusFull: "16px",
                          borderRadiusLarge: "12px",
                          borderRadiusMedium: "8px",
                        },
                      },
                    },
                  }}
                  onSubmit={async (formData) => {
                    await buy.mutateAsync(formData);
                  }}
                  onError={(err) => {
                    console.error("[Bricks] error", err);
                    toast.error(isEs ? "Error en el formulario de pago. Reintenta." : "Payment form error. Retry.");
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function SummaryStat({ icon: Icon, label, value }) {
  return (
    <div className="p-3 rounded-xl bg-cream-100 border border-cream-300">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-500">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="font-mono-num text-lg font-medium text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function CheckoutResult({ result, pack, T, isEs, navigate }) {
  const approved = result.status === "approved";
  const ICON = approved ? CheckCircle2 : Clock;
  const COLOR = approved ? "text-emerald-600" : "text-amber-600";
  const TITLE = approved
    ? (isEs ? "¡Pago aprobado!" : "Payment approved!")
    : (isEs ? "Pago en proceso" : "Payment processing");
  const SUB = approved
    ? (isEs ? "Tu pack está disponible en tu cuenta. Te enviamos el recibo por email."
            : "Your pack is ready. We sent the receipt to your inbox.")
    : (isEs ? "El pago está procesándose. Te avisamos cuando se confirme."
            : "Payment is still processing. We'll notify you when it confirms.");

  return (
    <Layout>
      <div className="max-w-lg mx-auto text-center py-12 fade-up" data-testid="pack-checkout-result">
        <div className={`mx-auto h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-6 ${COLOR}`}>
          <ICON className="h-8 w-8" />
        </div>
        <h1 className="font-serif-display text-4xl font-medium text-slate-900 tracking-tight" data-testid="pack-checkout-result-title">{TITLE}</h1>
        <p className="text-slate-600 mt-3">{SUB}</p>

        <div className="mt-7 rounded-2xl border border-cream-300 bg-white p-5 text-left space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">{isEs ? "Pack" : "Pack"}</span>
            <span className="font-semibold text-slate-900">{pack.name}</span>
          </div>
          {result.mp_order_id && (
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">{isEs ? "Orden MP" : "MP Order"}</span>
              <button /* design-system: ALLOW legacy-todo */
                onClick={() => {
                  navigator.clipboard?.writeText(result.mp_order_id);
                  toast.success(isEs ? "Copiado" : "Copied");
                }}
                className="font-mono text-xs text-slate-700 hover:text-slate-900"
                data-testid="pack-checkout-mp-order-id"
                title={result.mp_order_id}
              >
                {result.mp_order_id.length > 22 ? `${result.mp_order_id.slice(0, 22)}…` : result.mp_order_id}
              </button>
            </div>
          )}
          {result.units > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">{T.units}</span>
              <span className="font-semibold">{result.units}</span>
            </div>
          )}
          {result.valid_for_days > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">{T.validity}</span>
              <span className="font-semibold">{result.valid_for_days} {T.days}</span>
            </div>
          )}
        </div>

        <div className="mt-7 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:justify-center">
          <Button
            data-testid="pack-checkout-go-my-plan"
            onClick={() => navigate("/my-plan")}
            className="bg-emerald-600 hover:bg-emerald-700 rounded-full px-7 gap-2 h-12 sm:h-10 text-base sm:text-sm font-semibold"
          >
            <Sparkles className="h-4 w-4" /> {T.seeMyPlan}
          </Button>
          <Button
            data-testid="pack-checkout-go-dashboard"
            variant="outline"
            onClick={() => navigate("/dashboard")}
            className="rounded-full px-7 h-12 sm:h-10"
          >
            {T.backToDash}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
